import React from "react";
import { apiStokTindakLanjut, apiStokKeputusan, formatApiErrorDetail } from "@/lib/api";
import { qty } from "@/lib/format";

// Owner: material of cancelled notas whose SPK was already at a machine
// (F1-E, requirement 10.4). Per bahan: TERBUANG (wasted; stock was already
// cut when the nota was issued) or KEMBALIKAN (back to stock), with a reason.
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
const inputCls = "rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100 outline-none focus:border-amber-500/60";

function Baris({ nota, g, b, onDone }) {
  const [f, setF] = React.useState({ qty: qty(b.sisa_milli).replace(/\./g, "").replace(",", "."), alasan: "" });
  const [error, setError] = React.useState(null);
  const kirim = async (keputusan) => {
    setError(null);
    try {
      await apiStokKeputusan(nota.nota_id, { spk_id: g.spk_id, bahan_id: b.bahan_id, keputusan, qty: String(f.qty).replace(",", "."), alasan: f.alasan });
      onDone();
    } catch (e) { setError(errText(e)); }
  };
  return (
    <div data-testid={`sk-row-${nota.nomor_nota}-${b.kode}`} className="flex flex-wrap items-center gap-2 py-1">
      <span className="font-mono text-amber-300 w-40">{b.kode}</span>
      <span className="text-xs text-slate-400 w-56">menunggu {qty(b.sisa_milli)} {b.satuan} (dari {qty(b.qty_milli)}{b.terbuang_milli ? `, terbuang ${qty(b.terbuang_milli)}` : ""}{b.dikembalikan_milli ? `, kembali ${qty(b.dikembalikan_milli)}` : ""})</span>
      <input data-testid="sk-qty" type="number" step="0.001" min="0.001" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} className={`${inputCls} w-24`} />
      <input data-testid="sk-alasan" value={f.alasan} onChange={(e) => setF({ ...f, alasan: e.target.value })} placeholder="Alasan (wajib)" className={`${inputCls} flex-1 min-w-[12rem]`} />
      <button data-testid="sk-terbuang" disabled={f.alasan.trim().length < 5} onClick={() => kirim("TERBUANG")} className="rounded-md border border-red-500/40 px-2.5 py-1.5 text-xs text-red-300 disabled:opacity-50">Terbuang</button>
      <button data-testid="sk-kembalikan" disabled={f.alasan.trim().length < 5} onClick={() => kirim("KEMBALIKAN")} className="rounded-md border border-emerald-500/40 px-2.5 py-1.5 text-xs text-emerald-300 disabled:opacity-50">Kembalikan ke stok</button>
      {error && <span className="basis-full text-xs text-red-300">{error}</span>}
    </div>
  );
}

export function StokKeputusanPanel() {
  const [rows, setRows] = React.useState(null);
  const [error, setError] = React.useState(null);
  const load = React.useCallback(() => apiStokTindakLanjut().then(setRows).catch((e) => setError(errText(e))), []);
  React.useEffect(() => { load(); }, [load]);
  if (error) return <p className="text-xs text-red-300">{error}</p>;
  if (!rows) return null;
  return (
    <div data-testid="stok-keputusan-panel" className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-slate-200">Keputusan stok nota batal <span className="text-slate-500">({rows.length})</span></h2>
        <p className="text-xs text-slate-500">Bahan SPK yang sudah masuk mesin saat nota dibatalkan tidak dikembalikan otomatis. Pilih per bahan: terbuang atau dikembalikan ke stok (boleh sebagian).</p>
      </div>
      {rows.length === 0 && <p className="text-xs text-slate-500">Tidak ada bahan yang menunggu keputusan.</p>}
      {rows.map((n) => (
        <div key={n.nota_id} data-testid={`sk-nota-${n.nomor_nota}`} className="rounded-lg border border-slate-700 p-3 space-y-1">
          <div className="text-sm"><span className="font-mono text-amber-300">{n.nomor_nota}</span> <span className="text-slate-400">· {n.customer} · batal: {n.alasan_batal}</span></div>
          {n.stok.rincian.filter((g) => g.bahan.some((b) => b.sisa_milli > 0)).map((g) => (
            <div key={g.spk_id} className="pl-3 border-l border-slate-700">
              <div className="text-xs text-slate-400">SPK {g.nomor_spk} ({g.grup_spk}) — sudah diproses di mesin</div>
              {/* keyed by the remaining qty so the form resets to the new remainder after each decision */}
              {g.bahan.filter((b) => b.sisa_milli > 0).map((b) => <Baris key={`${b.bahan_id}-${b.sisa_milli}`} nota={n} g={g} b={b} onDone={load} />)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
